import React from 'react';
import { motion } from 'framer-motion';
import { AppStoreButton, PlayStoreButton } from './ui/StoreButtons';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar } = FiIcons;

const Hero = () => {
  return (
    <section className="relative pt-12 pb-20 lg:pt-24 lg:pb-32 overflow-hidden">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[400px] h-[400px] bg-teal/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center">

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 text-center lg:text-left mb-16 lg:mb-0"
          >
            {/* Rating Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber/10 text-amber-dark font-semibold text-sm mb-6 border border-amber/20">
              {[1,2,3,4,5].map(i => (
                <SafeIcon key={i} icon={FiStar} className="text-amber text-xs fill-amber" />
              ))}
              <span className="ml-1">4.9 · Loved by 10,000+ players</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary leading-tight mb-6">
              Get Better at Pickleball.{' '}
              <span className="text-primary">Faster.</span>
            </h1>

            <p className="text-xl text-text-secondary mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Just hit record. We watch your match, spot what's holding you back, and show you exactly how to fix it.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8">
              <AppStoreButton />
              <PlayStoreButton />
            </div>

            <p className="text-text-muted text-sm">Free to download · No equipment needed · Works on any phone</p>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[360px]">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-gradient-to-tr from-primary to-teal rounded-[3rem] transform rotate-6 scale-105 opacity-20 blur-2xl" />

              {/* Phone Frame */}
              <div className="relative rounded-[3rem] border-[10px] border-[#1A1A2E] bg-[#1A1A2E] overflow-hidden shadow-float aspect-[9/19.5]">
                <div className="absolute top-0 inset-x-0 flex justify-center z-20 pt-1">
                  <div className="w-24 h-5 bg-[#1A1A2E] rounded-b-2xl" />
                </div>

                {/* Green app-like background */}
                <div className="absolute inset-0 bg-neutral-bg" />

                {/* App UI Mockup */}
                <div className="absolute inset-0 flex flex-col pt-8 px-3 pb-3">
                  {/* Top bar */}
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div>
                      <p className="text-[9px] text-text-muted font-medium">TODAY'S SESSION</p>
                      <p className="text-sm font-bold text-text-primary">Match Analysis</p>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-white" />
                    </div>
                  </div>

                  {/* Score card */}
                  <div className="bg-primary rounded-2xl p-3 mb-3 text-white">
                    <p className="text-[9px] uppercase tracking-widest opacity-70 mb-1">Overall Score</p>
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold leading-none">87</span>
                      <span className="text-sm opacity-80 mb-1">/100</span>
                      <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">↑ +6 pts</span>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-1.5 mb-3">
                    {[
                      { label: 'Serve', val: '91%' },
                      { label: 'Dink', val: '78%' },
                      { label: 'Drive', val: '84%' },
                    ].map((s) => (
                      <div key={s.label} className="bg-white rounded-xl p-2 text-center border border-neutral-border">
                        <p className="text-sm font-bold text-primary">{s.val}</p>
                        <p className="text-[8px] text-text-muted">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Drill card */}
                  <div className="bg-white rounded-2xl p-3 border border-neutral-border mb-3">
                    <p className="text-[9px] text-text-muted uppercase tracking-wider mb-1">Recommended Drill</p>
                    <p className="text-xs font-bold text-text-primary mb-1">Cross-Court Dink Consistency</p>
                    <div className="flex items-center gap-1">
                      <div className="flex-1 bg-neutral-divider rounded-full h-1.5">
                        <div className="bg-teal h-1.5 rounded-full w-[65%]" />
                      </div>
                      <span className="text-[9px] text-teal font-semibold">65%</span>
                    </div>
                  </div>

                  {/* CTA button */}
                  <button className="w-full bg-primary text-white text-xs font-semibold py-2.5 rounded-xl mt-auto">
                    Start Today's Drill →
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Hero;